// Read-only client for the policy rule engine (Phase 2).
//
// The catalog is served by the backend rather than duplicated here on purpose:
// 44 rules with their param schemas are the engine's vocabulary, and a second
// copy in TypeScript would drift the moment someone adds a rule.

import api from './api';

export type PolicySource = 'branch' | 'global' | 'catalog_default' | 'off';

export interface PolicyParamSpec {
  name: string;
  type: string;
  default: unknown;
  choices: string[];
  label: string;
  help: string;
  item_fields: PolicyParamSpec[];
}

export interface PolicyRuleSpec {
  key: string;
  section: string;
  title: string;
  question: string;
  when_absent: 'default' | 'off';
  windowed: boolean;
  predicated: boolean;
  replaces: string | null;
  notes: string;
  params: PolicyParamSpec[];
  defaults: Record<string, unknown>;
}

export interface PolicyRuleRow {
  id: string;
  rule_key: string;
  scope: 'branch' | 'global';
  params: Record<string, unknown>;
  applies_to: Record<string, unknown>;
  effective_from: string | null;
  effective_to: string | null;
  priority: number;
  notes: string | null;
  auto_generated: boolean;
  created_at: string | null;
}

export interface EffectiveRule {
  key: string;
  title: string;
  question: string;
  source: PolicySource;
  effective_params: Record<string, unknown> | null;
  is_active: boolean;
  rows: PolicyRuleRow[];
  has_predicated_rows: boolean;
  has_windowed_rows: boolean;
}

export interface EffectiveSection {
  id: string;
  label: string;
  rules: EffectiveRule[];
}

export interface EffectivePolicy {
  branchID: string | null;
  on_date: string;
  employee_id: string | null;
  can_manage: boolean;
  editable: boolean;
  configured_rule_count: number;
  sections: EffectiveSection[];
}

export interface PolicyCoverage {
  branches_with_settings: number;
  branches_with_rules: number;
  pending: string[];
  legacy_bridge_removable: boolean;
  note: string;
}

export interface PolicyExplanation {
  rule_key: string;
  title: string;
  question: string;
  on_date: string;
  source: PolicySource;
  winning_rule_id: string | null;
  effective_params: Record<string, unknown> | null;
  considered: Array<{
    id: string;
    scope: 'branch' | 'global';
    priority: number;
    applies_to: Record<string, unknown>;
    window: [string | null, string | null];
    in_window: boolean;
    matched: boolean;
    notes: string | null;
  }>;
}

export async function getPolicyCatalog(): Promise<{
  sections: Array<{ id: string; label: string; rules: PolicyRuleSpec[] }>;
  predicates: Record<string, string>;
  total_rules: number;
}> {
  const { data } = await api.get('/policy-rules/catalog');
  return data;
}

export async function getEffectivePolicy(params?: {
  employeeId?: string;
  onDate?: string;
}): Promise<EffectivePolicy> {
  const { data } = await api.get('/policy-rules', {
    params: {
      ...(params?.employeeId ? { employee_id: params.employeeId } : {}),
      ...(params?.onDate ? { on_date: params.onDate } : {}),
    },
  });
  return data;
}

export async function explainPolicyRule(
  ruleKey: string,
  params?: { employeeId?: string; onDate?: string },
): Promise<PolicyExplanation> {
  const { data } = await api.get(`/policy-rules/explain/${ruleKey}`, {
    params: {
      ...(params?.employeeId ? { employee_id: params.employeeId } : {}),
      ...(params?.onDate ? { on_date: params.onDate } : {}),
    },
  });
  return data;
}

// Admin-only. Reports which branches still read policy from SalarySettings via
// the temporary Phase 1 legacy bridge.
export async function getPolicyCoverage(): Promise<PolicyCoverage> {
  const { data } = await api.get('/policy-rules/coverage');
  return data;
}

export interface RuleWritePayload {
  rule_key?: string;
  params?: Record<string, unknown>;
  applies_to?: Record<string, unknown>;
  effective_from?: string | null;
  effective_to?: string | null;
  priority?: number;
  notes?: string | null;
}

// A live rule is never mutated: the server closes it and inserts a successor so
// recalculating an earlier month still sees the policy that applied then.
// `mode` reports which path ran.
export interface RuleWriteResult {
  mode: 'updated_in_place' | 'superseded';
  rule: PolicyRuleRow;
  superseded: PolicyRuleRow | null;
}

export async function createPolicyRule(body: RuleWritePayload): Promise<PolicyRuleRow> {
  const { data } = await api.post('/policy-rules', body);
  return data;
}

export async function updatePolicyRule(
  ruleId: string,
  body: RuleWritePayload,
): Promise<RuleWriteResult> {
  const { data } = await api.put(`/policy-rules/${ruleId}`, body);
  return data;
}

export async function deletePolicyRule(
  ruleId: string,
): Promise<{ mode: 'deleted' | 'closed'; rule?: PolicyRuleRow; rule_id?: string }> {
  const { data } = await api.delete(`/policy-rules/${ruleId}`);
  return data;
}

export async function getPolicyRuleHistory(
  ruleKey: string,
): Promise<{ rule_key: string; rows: PolicyRuleRow[] }> {
  const { data } = await api.get(`/policy-rules/history/${ruleKey}`);
  return data;
}
